import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, ArrowRight, AlertCircle, CheckCircle, Eye, EyeOff, Key, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { appConfig } from '../../config/app.config';
import { useNavigate } from 'react-router-dom';

export function AlphaLogin() {
  const navigate = useNavigate();
  const [step, setStep] = useState('token'); // 'token', 'password', 'login'
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Step 1: Validate Token
  const handleTokenSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${appConfig.apiUrl}/api/alpha/validate-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: token.trim() })
      });

      const data = await response.json();

      if (response.ok && data.valid) {
        setEmail(data.email);
        if (data.passwordSet) {
          setStep('login');
        } else {
          setStep('password');
        }
      } else {
        setError(data.message || 'Invalid token. Please check and try again.');
      }
    } catch (err) {
      setError('Failed to validate token. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Set Password
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${appConfig.apiUrl}/api/alpha/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: token.trim(), password })
      });

      const data = await response.json();

      if (response.ok) {
        // Store user data
        localStorage.setItem('user', JSON.stringify(data.user));
        navigate('/dashboard');
      } else {
        setError(data.error || 'Failed to set password. Please try again.');
      }
    } catch (err) {
      setError('Failed to set password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Login
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${appConfig.apiUrl}/api/alpha/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: loginEmail || email, 
          password: loginPassword 
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Store user data
        localStorage.setItem('user', JSON.stringify(data.user));
        navigate('/dashboard');
      } else {
        setError(data.error || 'Login failed. Please check your credentials.');
      }
    } catch (err) {
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderTokenStep = () => (
    <motion.div
      key="token"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
    >
      <form onSubmit={handleTokenSubmit}>
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Access Token
          </label>
          <Input
            type="text"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Enter your access token"
            required
            className="w-full text-center font-mono"
            autoFocus
          />
          <p className="text-xs text-gray-500 mt-2">
            Enter the access token you received to get started
          </p>
        </div>

        <Button
          type="submit"
          disabled={loading || !token.trim()}
          className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
        >
          {loading ? (
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              Validating...
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Key className="h-4 w-4" />
              Validate Token
            </div>
          )}
        </Button>
      </form>

      <div className="mt-4 text-center">
        <button
          onClick={() => setStep('login')}
          className="text-sm text-purple-400 hover:text-purple-300 underline"
        >
          Already have an account? Sign in
        </button>
      </div>
    </motion.div>
  );

  const renderPasswordStep = () => (
    <motion.div
      key="password"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
    >
      {/* Success indicator */}
      <motion.div
        className="mb-6 p-3 bg-green-500/10 border border-green-500/30 rounded-lg"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div className="flex items-center gap-2">
          <CheckCircle className="h-4 w-4 text-green-400" />
          <div>
            <span className="text-sm text-green-300">Token validated!</span>
            <p className="text-xs text-green-400 mt-1">{email}</p>
          </div>
        </div>
      </motion.div>

      <form onSubmit={handlePasswordSubmit}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Set Your Password
            </label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter a secure password"
                required
                minLength={8}
                className="w-full pr-10"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-200"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Confirm Password
            </label>
            <Input
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              required
              minLength={8}
              className="w-full"
            />
          </div>

          <div className="text-xs text-gray-500 space-y-1">
            <p>Password requirements:</p>
            <ul className="list-disc list-inside pl-2 space-y-0.5">
              <li className={password.length >= 8 ? 'text-green-400' : ''}>At least 8 characters</li>
              <li className={password === confirmPassword && password ? 'text-green-400' : ''}>Passwords match</li>
            </ul>
          </div>
        </div>

        <Button
          type="submit"
          disabled={loading || !password || !confirmPassword || password !== confirmPassword}
          className="w-full mt-6 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
        >
          {loading ? (
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              Setting Password...
            </div>
          ) : (
            <div className="flex items-center gap-2">
              Complete Setup
              <ArrowRight className="h-4 w-4" />
            </div>
          )}
        </Button>
      </form>
    </motion.div>
  );

  const renderLoginStep = () => (
    <motion.div
      key="login"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
    >
      <form onSubmit={handleLoginSubmit}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Email Address
            </label>
            <Input
              type="email"
              value={loginEmail || email}
              onChange={(e) => setLoginEmail(e.target.value)}
              placeholder="your@email.com"
              required
              className="w-full"
              autoFocus={!email}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Password
            </label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                placeholder="Enter your password"
                required
                className="w-full pr-10"
                autoFocus={!!email}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-200"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>

        <Button
          type="submit"
          disabled={loading || !loginEmail && !email || !loginPassword}
          className="w-full mt-6 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
        >
          {loading ? (
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              Signing In...
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Sign In
            </div>
          )}
        </Button>
      </form>

      <div className="mt-4 text-center">
        <button
          onClick={() => {
            setStep('token');
            setEmail('');
            setToken('');
          }}
          className="text-sm text-purple-400 hover:text-purple-300 underline"
        >
          Have a new token? Click here
        </button>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#151415] to-[#2a282a] flex items-center justify-center p-4">
      <motion.div
        className="max-w-md w-full"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <motion.div
            className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mb-4"
            whileHover={{ scale: 1.1 }}
          >
            <Shield className="h-8 w-8 text-white" />
          </motion.div>
          <h1 className="text-3xl font-bold text-gray-100 mb-2">
            DigiTables Alpha
          </h1>
          <p className="text-gray-400">
            {step === 'token' && 'Enter your access token to get started'}
            {step === 'password' && 'Set up your password'}
            {step === 'login' && 'Sign in to your account'}
          </p>
        </div>

        {/* Form Card */}
        <motion.div
          className="bg-[#2a282a] rounded-lg shadow-xl p-6"
          layout
        >
          <AnimatePresence mode="wait">
            {step === 'token' && renderTokenStep()}
            {step === 'password' && renderPasswordStep()}
            {step === 'login' && renderLoginStep()}
          </AnimatePresence>

          {error && (
            <motion.div
              className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-red-400" />
                <span className="text-sm text-red-300">{error}</span>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Alpha Notice */}
        <motion.div
          className="mt-6 p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-orange-400 mt-0.5" />
            <div className="text-xs text-orange-300">
              <p className="font-semibold mb-1">Alpha Version - Limited Access</p>
              <p>Welcome to DigiTables Alpha! You have 100MB of storage space and access to all core features. Your feedback helps us improve the platform.</p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}